import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  GraduationCap,
  Users,
  ClipboardList,
  LogOut,
  ChevronRight,
  Calendar,
  Clock
} from 'lucide-react';
import { formatLocalDate } from '../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const TeacherPortal = () => {
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('teacher_token');
    const teacherData = localStorage.getItem('teacher_data');
    
    if (!token || !teacherData) {
      navigate('/teacher/login');
      return;
    }

    setTeacher(JSON.parse(teacherData));
    fetchStudents(token);
  }, [navigate]);

  const fetchStudents = async (token) => {
    try {
      const response = await axios.get(`${API}/api/teacher/my-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data.students);
    } catch (error) {
      console.error('Error fetching students:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('teacher_token');
    localStorage.removeItem('teacher_data');
    navigate('/teacher/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Teacher Portal</h1>
              <p className="text-sm text-gray-500">Welcome, {teacher?.name}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} className="text-gray-500">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            My Students
          </h2>
          <p className="text-gray-600 mt-1">Select a student to submit a daily or weekly check-in</p>
        </div>

        {students.length === 0 ? (
          <Card className="p-8 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Yet</h3>
            <p className="text-gray-500">
              You'll see students here once parents share their access code with you.
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {students.map((student) => (
              <Card
                key={student.id}
                className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/teacher/report/${student.id}`)}
                data-testid={`student-card-${student.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-xl font-bold text-primary">
                        {student.child_name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{student.child_name}</h3>
                      <p className="text-sm text-gray-500">Parent: {student.parent_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>Connected {formatLocalDate(student.activated_at)}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Quick Info */}
        <Card className="mt-8 p-6 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-4">
            <ClipboardList className="w-8 h-8 text-blue-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Quick Daily Check-In</h3>
              <p className="text-sm text-blue-800">
                The check-in form takes about 1-2 minutes to complete. Your observations help 
                parents track their child's symptoms and identify patterns between home and school.
              </p>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default TeacherPortal;
